// MeetingHistory.jsx
// The "Meeting History" tab. A read-only list of past meetings with a status
// icon per row: completed (blue check), rescheduled (amber), cancelled (red).
// Visible to both students and teachers.

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "../../features/auth/AuthContext"; // <-- adjust path
import { Link, useNavigate, useLocation } from "react-router";
import { getMeetingHistory } from "./meetingService";
import { toDisplayDateTime, to12hLabel, counterpartyName, convertTo12Hour } from "./meetingUtils";
import { MEETING_ICON } from "./meetingIcons";

const getMeetingStatusLabel = (status) => {
  const statusMap = {
    1: "scheduled",
    2: "completed",
    3: "cancelled",
    4: "rescheduled",
  };
  return statusMap[status] || "scheduled";
};

// Map a status to its badge colour class, the symbol shown inside the circle,
// and the coloured verb used in the sentence.
// Coloured verb per status; the icon comes from MEETING_ICON.
const STATUS_META = {
  scheduled: { cls: "bm-status-completed", verb: "scheduled" },
  completed: { cls: "bm-status-completed", verb: "completed" },
  rescheduled: { cls: "bm-status-rescheduled", verb: "rescheduled" },
  cancelled: { cls: "bm-status-cancelled", verb: "cancelled" },
};

const MeetingHistory = ({ refreshKey, highlightMeetingId }) => {
  const { token } = useAuth();
  const role = useSelector((s) => s.globalState?.role);
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [highlightedMeetingId, setHighlightedMeetingId] = useState(null);
  const globalState = useSelector((s) => s.globalState);

  // Handle highlighting when highlightMeetingId prop changes
  useEffect(() => {
    if (highlightMeetingId) {
      setHighlightedMeetingId(highlightMeetingId);
      // Auto-remove highlight after 4 seconds
      const timeout = setTimeout(() => {
        setHighlightedMeetingId(null);
        // Clear the highlightMeetingId from location state to prevent re-highlighting on tab change or refresh
        navigate(location.pathname, { replace: true, state: { activeTab: location.state?.activeTab } });
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [highlightMeetingId, navigate, location]);
  
  useEffect(() => {
    if (highlightedMeetingId) {
      const el = document.querySelector(`[data-meeting-id="${highlightedMeetingId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, highlightedMeetingId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      const res = await getMeetingHistory(token);
      if (!alive) return;
      setLoading(false);
      if (res.ok) setItems(res.data || []);
      else setError(res.error);
    })();
    return () => {
      alive = false;
    };
  }, [token, refreshKey]);

  if (loading) return <div className="bm-tab-loading">Loading…</div>;
  // Hide auth errors (401) and server errors (5xx), show as empty state instead
  if (error && !error.toLowerCase().includes("invalid token") && !error.match(/\b5\d{2}\b/))
    return <p className="bm-error">{error}</p>;
  if (items.length === 0)
    return <div className="bm-tab-empty">No meeting history yet.</div>;

  return (
    <div className="bm-history border border-[var(--bm-border)] rounded-lg">
      {items.map((item) => {
        const meta = STATUS_META[item.status] || STATUS_META.completed;
        const iconSrc = MEETING_ICON[item.status_label] || MEETING_ICON.completed;
        const isTeacher = role === "Teacher";

        return (
          <div className={`bm-history-row ${highlightedMeetingId === item.id ? "bm-meeting-card-highlighted" : ""}`} key={item.id} data-meeting-id={item.id}>
            <img src={iconSrc} className="bm-history-icon-img" alt="" />
            <div>
            <p className="bm-history-text">
              {isTeacher ? (
                // Teacher view: "A 1-on-1 session with Student Name was completed for 01/04/2025 from 10:00 AM to 10:30 AM (30 Min). Course: English, Price: $2"
                <>
                  A <strong>{item.session_type_name || "1-on-1"} session</strong> with{" "}
                  <strong>
                    <Link to={`/student/${item.student_id || item.id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                      {item.student_name}
                    </Link>
                  </strong>{" "}
                  was <span className={meta.cls}><strong>{getMeetingStatusLabel(item.status)}</strong></span> for{" "}
                  <strong>
                    {toDisplayDateTime(item.local_date, item.local_start_time).split(", at ")[0]} from {convertTo12Hour(item.local_start_time)} to {convertTo12Hour(item.local_end_time)}
                  ({item.duration_minutes} Min).
                  </strong>
                </>
              ) : (
                // Student view: "Meeting with Shubhangi was completed for 25/08/2026, at 09:00 AM. Course: 1-on-1 Session, Price: $2.00"
                <>
                  Meeting with{" "}
                  <strong>
                    <Link to={`/teacher/${item.teacher_id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                      {item.teacher_name || counterpartyName(item, role)}
                    </Link>
                  </strong>{" "}
                  was <span className={meta.cls}><strong>{getMeetingStatusLabel(item.status)}</strong></span> for{" "}
                  <strong>
                    {toDisplayDateTime(item.local_date, item.local_start_time)}.
                  </strong>
                </>
              )}
            </p>
              <p className="bm-history-text">
                <strong>
                  {item.course_name && `Course: ${item.course_name}, `}
                </strong>
                {globalState?.enabled_modules?.includes("marketplace") && (
                  <><strong>Price: {item.price_currency_symbol || '$'}{item.price_amount}</strong></>
                )}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MeetingHistory;